/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

// @mui material components
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiButton from 'components/VuiButton';
import VuiSelect from 'components/VuiSelect';

// Vision UI Dashboard PRO React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MiniStatisticsCard from 'examples/Cards/StatisticsCards/MiniStatisticsCard';
import DefaultDoughnutChart from 'examples/Charts/DoughnutCharts/DefaultDoughnutChart';
import VerticalBarChart from 'examples/Charts/BarCharts/VerticalBarChart';

// Overview dashboard components
import Highlights from './components/Highlights';
import ContentApproval from './components/ContentApproval';
import ComplianceUpdate from './components/ComplianceUpdate';

// Data
import { donutChartData, donutChartOptions } from './data/donutChartData';
import { barChartData, barChartOptions } from './data/barChartData';

// React icons
import { IoDocument } from 'react-icons/io5';
import { IoCheckmarkDone } from 'react-icons/io5';
import { IoTime } from 'react-icons/io5';

// React hooks
import { useState, useEffect } from 'react';

// API Services
import { takedownsApi } from 'services/takedownsApi';

// @mui material components
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

function Overview() {
  // Date filter state
  const [selectedDateFilter, setSelectedDateFilter] = useState('all_time');
  
  // Infringement Activity filter states
  const [firstFilter, setFirstFilter] = useState({ value: 'takedowns', label: 'Takedowns' });
  const [secondFilter, setSecondFilter] = useState({ value: 'infringement_types', label: 'Infringement Types' });
  
  // Takedowns By filter state
  const [takedownsByFilter, setTakedownsByFilter] = useState({ value: 'removed', label: 'Removed' });

  // API Data states
  const [statsData, setStatsData] = useState({
    takedowns_submitted: 0,
    takedowns_completed: 0,
    takedowns_pending: 0,
    totalTakedownsSubmitted: 0
  });
  const [infringementActivityData, setInfringementActivityData] = useState(null);
  const [topPlatformsData, setTopPlatformsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date filter options
  const dateFilterOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'year_to_date', label: 'Year to Date' },
    { value: 'all_time', label: 'All Time' }
  ];

  // Infringement Activity filter options
  const infringementFilterOptions = [
    { value: 'takedowns', label: 'Takedowns' },
    { value: 'infringement_types', label: 'Infringement Types' },
    { value: 'threat_actors', label: 'Threat Actors' },
    { value: 'platforms', label: 'Platforms' }
  ];

  // Takedowns By filter options
  const takedownsByOptions = [
    { value: 'removed', label: 'Removed' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'pending', label: 'Pending' }
  ];

  // Fetch data functions
  const fetchStats = async (dateRange) => {
    try {
      const data = await takedownsApi.fetchOverviewStats(dateRange);
      setStatsData(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics');
    }
  };

  const fetchInfringementActivity = async (dateRange, firstFilterValue, secondFilterValue) => {
    try {
      let data;
      
      console.log('=== Fetching Infringement Activity ===');
      console.log('Date Range:', dateRange);
      console.log('First Filter (What):', firstFilterValue);
      console.log('Second Filter (By):', secondFilterValue);
      console.log('Takedowns Card Status:', takedownsByFilter.value);
      
      // Determine which API to call based on the first filter (what we're analyzing)
      if (firstFilterValue === 'takedowns') {
        // When analyzing takedowns BY something, we want to show ALL takedown statuses combined
        // We need to fetch data for all statuses and aggregate them
        console.log('Fetching ALL takedown statuses for aggregation...');
        
        // Fetch all three statuses in parallel
        const [removedData, submittedData, rejectedData] = await Promise.all([
          takedownsApi.fetchInfringementActivityTakedowns(dateRange, 'Removed'),
          takedownsApi.fetchInfringementActivityTakedowns(dateRange, 'Submitted'),
          takedownsApi.fetchInfringementActivityTakedowns(dateRange, 'Rejected')
        ]);
        
        console.log('Received data for all statuses:', {
          removed: removedData,
          submitted: submittedData,
          rejected: rejectedData
        });
        
        // Aggregate the data based on the second filter
        if (secondFilterValue === 'infringement_types') {
          // For "Takedowns BY Infringement Types", we want to show the statuses as the main dimension
          // with infringement type breakdown available in tooltips/details
          const removedTotal = removedData.infringement_types?.reduce((sum, item) => sum + item.count, 0) || 0;
          const submittedTotal = submittedData.infringement_types?.reduce((sum, item) => sum + item.count, 0) || 0;
          const rejectedTotal = rejectedData.infringement_types?.reduce((sum, item) => sum + item.count, 0) || 0;
          const totalTakedownsSubmitted = removedTotal + submittedTotal;
          data = {
            takedown_statuses: [
              {
                status: 'Removed',
                count: removedTotal,
                breakdown: removedData.infringement_types || []
              },
              {
                status: 'Submitted',
                count: submittedTotal,
                breakdown: submittedData.infringement_types || []
              },
              {
                status: 'Rejected',
                count: rejectedTotal,
                breakdown: rejectedData.infringement_types || []
              }
            ]
          };
        } else if (secondFilterValue === 'threat_actors') {
          // For "Takedowns BY Threat Actors", show statuses with threat actor breakdown
          const removedTotal = removedData.threat_actors?.reduce((sum, item) => sum + item.count, 0) || 0;
          const submittedTotal = submittedData.threat_actors?.reduce((sum, item) => sum + item.count, 0) || 0;
          const rejectedTotal = rejectedData.threat_actors?.reduce((sum, item) => sum + item.count, 0) || 0;
          
          data = {
            takedown_statuses: [
              {
                status: 'Removed',
                count: removedTotal,
                breakdown: removedData.threat_actors || []
              },
              {
                status: 'Submitted',
                count: submittedTotal,
                breakdown: submittedData.threat_actors || []
              },
              {
                status: 'Rejected',
                count: rejectedTotal,
                breakdown: rejectedData.threat_actors || []
              }
            ]
          };
        } else if (secondFilterValue === 'platforms') {
          // For "Takedowns BY Platforms", show statuses with platform breakdown
          const removedTotal = removedData.platforms?.reduce((sum, item) => sum + item.count, 0) || 0;
          const submittedTotal = submittedData.platforms?.reduce((sum, item) => sum + item.count, 0) || 0;
          const rejectedTotal = rejectedData.platforms?.reduce((sum, item) => sum + item.count, 0) || 0;
          
          data = {
            takedown_statuses: [
              {
                status: 'Removed',
                count: removedTotal,
                breakdown: removedData.platforms || []
              },
              {
                status: 'Submitted',
                count: submittedTotal,
                breakdown: submittedData.platforms || []
              },
              {
                status: 'Rejected',
                count: rejectedTotal,
                breakdown: rejectedData.platforms || []
              }
            ]
          };
        }
        
        console.log('Aggregated takedowns data:', data);
      } else if (firstFilterValue === 'infringement_types') {
        // When analyzing infringement types, fetch all infringement types data
        console.log('Calling fetchInfringementActivityTypes');
        data = await takedownsApi.fetchInfringementActivityTypes(dateRange);
        console.log('Received infringement types data structure:', {
          hasData: !!data.data,
          dataCount: data.data?.length,
          firstItem: data.data?.[0] ? {
            violation_type: data.data[0].violation_type,
            hasTakedowns: !!data.data[0].takedowns,
            takedownsBreakdown: data.data[0].takedowns
          } : null
        });
      } else if (firstFilterValue === 'platforms') {
        // When analyzing platforms, fetch all platforms data
        console.log('Calling fetchInfringementActivityPlatforms');
        data = await takedownsApi.fetchInfringementActivityPlatforms(dateRange);
        console.log('Received platforms data:', data);
      } else if (firstFilterValue === 'threat_actors') {
        // When analyzing threat actors, fetch all threat actors data
        console.log('Calling fetchInfringementActivityThreatActors');
        data = await takedownsApi.fetchInfringementActivityThreatActors(dateRange);
        console.log('Received threat actors data:', data);
      }
      
      setInfringementActivityData(data);
      console.log('=== End Infringement Activity Fetch ===\n');
    } catch (err) {
      console.error('Error fetching infringement activity:', err);
      setError('Failed to load infringement activity');
    }
  };

  const fetchTopPlatforms = async (dateRange, status) => {
    try {
      console.log('Fetching top platforms with status:', { dateRange, status });
      // Map the filter value to API status
      const takedownStatus = status === 'removed' ? 'Removed' : 
                            status === 'submitted' ? 'Submitted' : 
                            status === 'pending' ? 'Pending' : 'Submitted';
      
      // Use infringement activity takedowns endpoint to get platforms by status
      const data = await takedownsApi.fetchInfringementActivityTakedowns(dateRange, takedownStatus);
      setTopPlatformsData(data);
      console.log('Fetched platforms data for status:', takedownStatus, data);
    } catch (err) {
      console.error('Error fetching top platforms:', err);
      setError('Failed to load top platforms');
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([
          fetchStats(selectedDateFilter),
          fetchInfringementActivity(selectedDateFilter, firstFilter.value, secondFilter.value),
          fetchTopPlatforms(selectedDateFilter, takedownsByFilter.value)
        ]);
      } catch (err) {
        console.error('Error fetching overview data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, [selectedDateFilter, firstFilter.value, secondFilter.value, takedownsByFilter.value]);

  // Handler functions
  const handleDateFilterChange = (dateValue) => {
    setSelectedDateFilter(dateValue);
    // In a real app, this would trigger a data refetch
    console.log('Date filter changed to:', dateValue);
  };

  const handleFirstFilterChange = (selectedOption) => {
    setFirstFilter(selectedOption);
    // Update second filter to exclude the selected first filter
    const availableOptions = infringementFilterOptions.filter(option => option.value !== selectedOption.value);
    if (availableOptions.length > 0) {
      setSecondFilter(availableOptions[0]);
    }
  };

  const handleSecondFilterChange = (selectedOption) => {
    setSecondFilter(selectedOption);
    // In a real app, this would trigger chart re-rendering
    console.log('Second filter changed to:', selectedOption);
  };

  const handleTakedownsByFilterChange = (selectedOption) => {
    setTakedownsByFilter(selectedOption);
    // In a real app, this would trigger chart re-rendering
    console.log('Takedowns By filter changed to:', selectedOption);
  };

  // Get available options for second filter (excluding first filter selection)
  const getSecondFilterOptions = () => {
    return infringementFilterOptions.filter(option => option.value !== firstFilter.value);
  };

  // Generate dynamic chart data based on filter selections and API data
  const getDynamicChartData = () => {
    if (!infringementActivityData) {
      // Return default empty data while loading
      return {
        data: [25, 25, 25, 25],
        labels: ['Loading...', 'Loading...', 'Loading...', 'Loading...'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      };
    }

    console.log('=== Generating Dynamic Chart Data ===');
    console.log('First Filter:', firstFilter.value, firstFilter.label);
    console.log('Second Filter:', secondFilter.value, secondFilter.label);
    console.log('Infringement Activity Data Keys:', Object.keys(infringementActivityData));

    const filterCombination = `${firstFilter.value}_by_${secondFilter.value}`;
    let chartData = { data: [], labels: [], colors: [] };

    // Map API data to chart format based on filter combination
    if (firstFilter.value === 'takedowns') {
      // "Takedowns BY X" - Shows the three takedown statuses (Removed, Submitted, Rejected)
      // with breakdown by the second dimension available in tooltips
      if (infringementActivityData.takedown_statuses) {
        chartData.labels = infringementActivityData.takedown_statuses.map(item => item.status || 'Unknown');
        chartData.data = infringementActivityData.takedown_statuses.map(item => item.count || 0);
        
        // Store breakdown data for tooltips (we'll need to enhance the chart component to use this)
        chartData.breakdown = infringementActivityData.takedown_statuses.map(item => ({
          status: item.status || 'Unknown',
          details: item.breakdown || []
        }));
        
        console.log('Takedowns by statuses with breakdown:', {
          labels: chartData.labels,
          data: chartData.data,
          breakdown: chartData.breakdown
        });
      }
    } else if (firstFilter.value === 'infringement_types') {
      // "Infringement Types BY X" - Shows each infringement type with breakdown stored for tooltips
      if (infringementActivityData.data && infringementActivityData.data.length > 0) {
        // Always show infringement types as the main labels
        chartData.labels = infringementActivityData.data.map(item => item.violation_type || 'Unknown');
        
        if (secondFilter.value === 'takedowns') {
          // Show each infringement type with its TOTAL takedown count (sum of all statuses)
          chartData.data = infringementActivityData.data.map(item => {
            const removed = Number(item.takedowns?.removed) || 0;
            const submitted = Number(item.takedowns?.submitted) || 0;
            const rejected = Number(item.takedowns?.rejected) || 0;
            const total = removed + submitted + rejected;
            return total;
          });
          console.log('Infringement Types by Takedowns (total):', { 
            labels: chartData.labels, 
            data: chartData.data,
            rawData: infringementActivityData.data.map(item => ({
              violation_type: item.violation_type,
              takedowns: item.takedowns
            }))
          });
        } else if (secondFilter.value === 'threat_actors') {
          // Show total count per infringement type
          chartData.data = infringementActivityData.data.map(item => {
            const total = item.threat_actors?.reduce((sum, actor) => sum + (actor.count || 0), 0) || 0;
            return total;
          });
          console.log('Infringement Types by Threat Actors:', { labels: chartData.labels, data: chartData.data });
        } else if (secondFilter.value === 'platforms') {
          // Show total count per infringement type
          chartData.data = infringementActivityData.data.map(item => {
            const total = item.platforms?.reduce((sum, platform) => sum + (platform.count || 0), 0) || 0;
            return total;
          });
          console.log('Infringement Types by Platforms:', { labels: chartData.labels, data: chartData.data });
        }
      }
    } else if (firstFilter.value === 'threat_actors') {
      // "Threat Actors BY X" - Shows top threat actors with breakdown stored for tooltips
      if (infringementActivityData.data && infringementActivityData.data.length > 0) {
        // Show top threat actors (up to 5)
        const topActors = infringementActivityData.data.slice(0, 5);
        chartData.labels = topActors.map(item => item.username || 'Unknown');
        
        if (secondFilter.value === 'takedowns') {
          // Show total takedowns per threat actor
          chartData.data = topActors.map(item => {
            const removed = Number(item.takedowns?.removed) || 0;
            const submitted = Number(item.takedowns?.submitted) || 0;
            const rejected = Number(item.takedowns?.rejected) || 0;
            return removed + submitted + rejected;
          });
          console.log('Threat Actors by Takedowns:', { labels: chartData.labels, data: chartData.data });
        } else if (secondFilter.value === 'infringement_types') {
          // Show total infringement types count per threat actor
          chartData.data = topActors.map(item => {
            const total = item.infringement_types?.reduce((sum, type) => sum + (type.count || 0), 0) || 0;
            return total;
          });
          console.log('Threat Actors by Infringement Types:', { labels: chartData.labels, data: chartData.data });
        } else if (secondFilter.value === 'platforms') {
          // Show total platforms count per threat actor
          chartData.data = topActors.map(item => {
            const total = item.platforms?.reduce((sum, platform) => sum + (platform.count || 0), 0) || 0;
            return total;
          });
          console.log('Threat Actors by Platforms:', { labels: chartData.labels, data: chartData.data });
        }
      }
    } else if (firstFilter.value === 'platforms') {
      // "Platforms BY X" - Shows top platforms with breakdown stored for tooltips
      if (infringementActivityData.data && infringementActivityData.data.length > 0) {
        // Show top platforms (up to 5)
        const topPlatforms = infringementActivityData.data.slice(0, 5);
        chartData.labels = topPlatforms.map(item => item.root_domain || 'Unknown');
        
        if (secondFilter.value === 'takedowns') {
          // Show total takedowns per platform
          chartData.data = topPlatforms.map(item => {
            const removed = Number(item.takedowns?.removed) || 0;
            const submitted = Number(item.takedowns?.submitted) || 0;
            const rejected = Number(item.takedowns?.rejected) || 0;
            return removed + submitted + rejected;
          });
          console.log('Platforms by Takedowns:', { labels: chartData.labels, data: chartData.data });
        } else if (secondFilter.value === 'infringement_types') {
          // Show total infringement types count per platform
          chartData.data = topPlatforms.map(item => {
            const total = item.infringement_types?.reduce((sum, type) => sum + (type.count || 0), 0) || 0;
            return total;
          });
          console.log('Platforms by Infringement Types:', { labels: chartData.labels, data: chartData.data });
        } else if (secondFilter.value === 'threat_actors') {
          // Show total threat actors count per platform
          chartData.data = topPlatforms.map(item => {
            const total = item.threat_actors?.reduce((sum, actor) => sum + (actor.count || 0), 0) || 0;
            return total;
          });
          console.log('Platforms by Threat Actors:', { labels: chartData.labels, data: chartData.data });
        }
      }
    }

    // Generate colors for the data (with safety check)
    const defaultColors = ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D', '#FFB547'];
    if (chartData.labels && chartData.labels.length > 0) {
      chartData.colors = chartData.labels.map((_, index) => defaultColors[index % defaultColors.length]);
    } else {
      chartData.colors = [];
    }

    console.log('Generated Chart Data:', {
      filterCombination,
      labelsCount: chartData.labels?.length || 0,
      labels: chartData.labels,
      data: chartData.data,
      dataTypes: chartData.data?.map(val => typeof val),
      hasNaN: chartData.data?.some(val => isNaN(val)),
      allNumbers: chartData.data?.every(val => typeof val === 'number' && !isNaN(val))
    });
    console.log('=== End Chart Data Generation ===\n');

    // Fallback to sample data if no API data
    if (chartData.data.length === 0) {
    const chartDataSets = {
      'takedowns_by_infringement_types': {
        data: [40, 30, 20, 10],
        labels: ['Copyright', 'Trademark', 'Privacy', 'Defamation'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'takedowns_by_threat_actors': {
        data: [35, 25, 20, 15, 5],
        labels: ['Actor A', 'Actor B', 'Actor C', 'Actor D', 'Actor E'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D', '#FFB547']
      },
      'takedowns_by_platforms': {
        data: [35, 25, 20, 10, 10],
        labels: ['Meta', 'X', 'TikTok', 'Snapchat', 'Instagram'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D', '#FFB547']
      },
      'infringement_types_by_takedowns': {
        data: [45, 25, 20, 10],
        labels: ['Type A', 'Type B', 'Type C', 'Type D'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'infringement_types_by_threat_actors': {
        data: [30, 25, 25, 20],
        labels: ['Actor X', 'Actor Y', 'Actor Z', 'Actor W'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'infringement_types_by_platforms': {
        data: [40, 30, 20, 10],
        labels: ['Platform 1', 'Platform 2', 'Platform 3', 'Platform 4'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'threat_actors_by_takedowns': {
        data: [35, 30, 20, 15],
        labels: ['Takedown 1', 'Takedown 2', 'Takedown 3', 'Takedown 4'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'threat_actors_by_infringement_types': {
        data: [40, 25, 20, 15],
        labels: ['Copyright', 'Trademark', 'Privacy', 'Defamation'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'threat_actors_by_platforms': {
        data: [35, 25, 25, 15],
        labels: ['Meta', 'X', 'TikTok', 'Snapchat'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'platforms_by_takedowns': {
        data: [30, 25, 25, 20],
        labels: ['Takedown A', 'Takedown B', 'Takedown C', 'Takedown D'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'platforms_by_infringement_types': {
        data: [35, 30, 20, 15],
        labels: ['Copyright', 'Trademark', 'Privacy', 'Defamation'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      },
      'platforms_by_threat_actors': {
        data: [40, 25, 20, 15],
        labels: ['Actor 1', 'Actor 2', 'Actor 3', 'Actor 4'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D']
      }
    };

    // Default data if combination not found
    const defaultData = {
      data: [35, 25, 20, 10, 10],
      labels: ['Meta', 'X', 'TikTok', 'Snapchat', 'Instagram'],
      colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D', '#FFB547']
    };

    return chartDataSets[filterCombination] || defaultData;
    }

    // Ensure chartData always has required properties to prevent undefined errors
    return {
      data: chartData.data || [],
      labels: chartData.labels || [],
      colors: chartData.colors || [],
      breakdown: chartData.breakdown || null
    };
  };

  // Get current chart data
  const currentChartData = getDynamicChartData();

  // Generate custom tooltip formatter based on the current filter combination
  const getTooltipFormatter = () => {
    if (!infringementActivityData) return null;

    const filterCombination = `${firstFilter.value}_by_${secondFilter.value}`;

    return {
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        try {
          // If dataPointIndex is null, use seriesIndex instead (common ApexCharts behavior)
          const actualIndex = dataPointIndex !== null && dataPointIndex !== undefined ? dataPointIndex : seriesIndex;
          
          // Use currentChartData.labels instead of w.globals.labels since that's more reliable
          const label = currentChartData.labels?.[actualIndex] || w?.globals?.labels?.[actualIndex] || 'Unknown';
          // For donut charts, series is an array of values, not nested arrays  
          const value = series?.[actualIndex] || 0;
          
          console.log('Tooltip Debug:', { 
            label, 
            value, 
            actualIndex, 
            dataPointIndex, 
            seriesIndex,
            series, 
            firstFilter: firstFilter.value,
            secondFilter: secondFilter.value,
            hasBreakdown: !!currentChartData.breakdown,
            breakdownLength: currentChartData.breakdown?.length
          });
          
          let tooltipContent = `<div class="custom-tooltip" style="background: rgba(0, 0, 0, 0.9); padding: 12px; border-radius: 8px; color: white; border: 1px solid rgba(255, 255, 255, 0.2); min-width: 200px;">`;
          tooltipContent += `<div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${label}: ${value}</div>`;
          
          // Add breakdown based on filter combination
          if (firstFilter.value === 'takedowns' && currentChartData.breakdown && currentChartData.breakdown[actualIndex]) {
            const breakdown = currentChartData.breakdown[actualIndex];
            tooltipContent += `<div style="font-size: 12px; color: rgba(255, 255, 255, 0.8); margin-top: 8px;">`;
            
            if (breakdown.details && breakdown.details.length > 0) {
              breakdown.details.slice(0, 5).forEach(item => {
                const itemLabel = item.violation_type || item.username || item.root_domain || 'Unknown';
                const itemCount = item.count || 0;
                tooltipContent += `<div style="margin-top: 4px;">• ${itemLabel}: ${itemCount}</div>`;
              });
              if (breakdown.details.length > 5) {
                tooltipContent += `<div style="margin-top: 4px; font-style: italic;">... and ${breakdown.details.length - 5} more</div>`;
              }
            }
            
            tooltipContent += `</div>`;
          } else if (firstFilter.value === 'infringement_types' && infringementActivityData.data) {
          const item = infringementActivityData.data.find(d => d.violation_type === label);
          if (item) {
            tooltipContent += `<div style="font-size: 12px; color: rgba(255, 255, 255, 0.8);">`;
            
            if (secondFilter.value === 'takedowns' && item.takedowns) {
              tooltipContent += `<div style="margin-top: 4px;">• Removed: ${item.takedowns.removed}</div>`;
              tooltipContent += `<div style="margin-top: 4px;">• Submitted: ${item.takedowns.submitted}</div>`;
              tooltipContent += `<div style="margin-top: 4px;">• Rejected: ${item.takedowns.rejected}</div>`;
            } else if (secondFilter.value === 'threat_actors' && item.threat_actors) {
              item.threat_actors.slice(0, 5).forEach(actor => {
                tooltipContent += `<div style="margin-top: 4px;">• ${actor.username}: ${actor.count}</div>`;
              });
              if (item.threat_actors.length > 5) {
                tooltipContent += `<div style="margin-top: 4px; font-style: italic;">... and ${item.threat_actors.length - 5} more</div>`;
              }
            } else if (secondFilter.value === 'platforms' && item.platforms) {
              item.platforms.slice(0, 5).forEach(platform => {
                tooltipContent += `<div style="margin-top: 4px;">• ${platform.root_domain}: ${platform.count}</div>`;
              });
              if (item.platforms.length > 5) {
                tooltipContent += `<div style="margin-top: 4px; font-style: italic;">... and ${item.platforms.length - 5} more</div>`;
              }
            }
            
            tooltipContent += `</div>`;
          }
        } else if (firstFilter.value === 'platforms' && infringementActivityData.data) {
          const item = infringementActivityData.data.find(d => d.root_domain === label);
          if (item) {
            tooltipContent += `<div style="font-size: 12px; color: rgba(255, 255, 255, 0.8);">`;
            
            if (secondFilter.value === 'takedowns' && item.takedowns) {
              tooltipContent += `<div style="margin-top: 4px;">• Removed: ${item.takedowns.removed}</div>`;
              tooltipContent += `<div style="margin-top: 4px;">• Submitted: ${item.takedowns.submitted}</div>`;
              tooltipContent += `<div style="margin-top: 4px;">• Rejected: ${item.takedowns.rejected}</div>`;
            } else if (secondFilter.value === 'infringement_types' && item.infringement_types) {
              item.infringement_types.slice(0, 5).forEach(type => {
                tooltipContent += `<div style="margin-top: 4px;">• ${type.violation_type}: ${type.count}</div>`;
              });
              if (item.infringement_types.length > 5) {
                tooltipContent += `<div style="margin-top: 4px; font-style: italic;">... and ${item.infringement_types.length - 5} more</div>`;
              }
            } else if (secondFilter.value === 'threat_actors' && item.threat_actors) {
              item.threat_actors.slice(0, 5).forEach(actor => {
                tooltipContent += `<div style="margin-top: 4px;">• ${actor.username}: ${actor.count}</div>`;
              });
              if (item.threat_actors.length > 5) {
                tooltipContent += `<div style="margin-top: 4px; font-style: italic;">... and ${item.threat_actors.length - 5} more</div>`;
              }
            }
            
            tooltipContent += `</div>`;
          }
        } else if (firstFilter.value === 'threat_actors' && infringementActivityData.data) {
          const item = infringementActivityData.data.find(d => d.username === label);
          if (item) {
            tooltipContent += `<div style="font-size: 12px; color: rgba(255, 255, 255, 0.8);">`;
            
            if (secondFilter.value === 'takedowns' && item.takedowns) {
              tooltipContent += `<div style="margin-top: 4px;">• Removed: ${item.takedowns.removed}</div>`;
              tooltipContent += `<div style="margin-top: 4px;">• Submitted: ${item.takedowns.submitted}</div>`;
              tooltipContent += `<div style="margin-top: 4px;">• Rejected: ${item.takedowns.rejected}</div>`;
            } else if (secondFilter.value === 'infringement_types' && item.infringement_types) {
              item.infringement_types.slice(0, 5).forEach(type => {
                tooltipContent += `<div style="margin-top: 4px;">• ${type.violation_type}: ${type.count}</div>`;
              });
              if (item.infringement_types.length > 5) {
                tooltipContent += `<div style="margin-top: 4px; font-style: italic;">... and ${item.infringement_types.length - 5} more</div>`;
              }
            } else if (secondFilter.value === 'platforms' && item.platforms) {
              item.platforms.slice(0, 5).forEach(platform => {
                tooltipContent += `<div style="margin-top: 4px;">• ${platform.root_domain}: ${platform.count}</div>`;
              });
              if (item.platforms.length > 5) {
                tooltipContent += `<div style="margin-top: 4px; font-style: italic;">... and ${item.platforms.length - 5} more</div>`;
              }
            }
            
            tooltipContent += `</div>`;
          }
        }
        
        tooltipContent += `</div>`;
        return tooltipContent;
        } catch (error) {
          console.error('Tooltip error:', error);
          return `<div style="background: rgba(0, 0, 0, 0.9); padding: 12px; border-radius: 8px; color: white;">Error loading tooltip</div>`;
        }
      }
    };
  };

  // Generate dynamic chart data for Takedowns By card using API data
  const getTakedownsByChartData = () => {
    if (!topPlatformsData || !topPlatformsData.platforms) {
      // Return default empty data while loading
      return {
        data: [{
          name: 'Loading...',
          data: [10, 10, 10, 10, 10]
        }],
        categories: ['Loading...', 'Loading...', 'Loading...', 'Loading...', 'Loading...'],
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D', '#FFB547']
      };
    }

    const filterValue = takedownsByFilter.value;
    const filterLabel = takedownsByFilter.label;
    
    // Use actual API data from platforms breakdown in infringement activity response
    // The response structure is: { platforms: [{ root_domain: "...", count: ... }] }
    const platforms = topPlatformsData.platforms.slice(0, 7);
    const categories = platforms.map(item => item.root_domain);
    const data = platforms.map(item => item.count);
    
    console.log('Takedowns chart data:', { filterLabel, platforms, categories, data });
    
    return {
        data: [{
        name: `${filterLabel} Takedowns`,
        data: data
        }],
      categories: categories,
        colors: ['#0075FF', '#00D4FF', '#7B61FF', '#FF6B9D', '#FFB547', '#FF6B9D', '#7B61FF']
    };
  };

  // Get current Takedowns By chart data
  const currentTakedownsByChartData = getTakedownsByChartData();
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            Overview Dashboard
          </VuiTypography>
        </VuiBox>

        {/* Date Filter Buttons */}
        <VuiBox mb={3}>
          <VuiBox display="flex" gap={1} flexWrap="wrap">
            {dateFilterOptions.map((option) => (
              <VuiButton
                key={option.value}
                variant={selectedDateFilter === option.value ? "contained" : "outlined"}
                color={selectedDateFilter === option.value ? "info" : "white"}
                size="small"
                onClick={() => handleDateFilterChange(option.value)}
                sx={{
                  fontSize: "12px",
                  fontWeight: "medium",
                  px: 2,
                  py: 1,
                  minHeight: "32px",
                  textTransform: "none",
                  border: selectedDateFilter === option.value ? "none" : "1px solid rgba(255, 255, 255, 0.2)",
                  '&:hover': {
                    backgroundColor: selectedDateFilter === option.value ? undefined : 'rgba(255, 255, 255, 0.05)',
                  }
                }}
              >
                {option.label}
              </VuiButton>
            ))}
          </VuiBox>
        </VuiBox>

        {/* Loading State */}
        {loading && (
          <VuiBox display="flex" justifyContent="center" py={8}>
            <CircularProgress color="info" />
          </VuiBox>
        )}

        {/* Error State */}
        {error && !loading && (
          <VuiBox mb={3}>
            <Alert severity="error" sx={{ backgroundColor: 'rgba(255, 107, 107, 0.1)', color: 'white' }}>
              {error}
            </Alert>
          </VuiBox>
        )}

        {/* Top Statistics Cards */}
        {!loading && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} lg={4}>
            <MiniStatisticsCard
              title={{ text: 'TOTAL TAKEDOWNS SUBMITTED', fontWeight: 'bold' }}
                count={statsData.takedowns_submitted?.toString() || '0'}
              icon={<IoDocument size="22px" color="white" />}
              direction="right"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MiniStatisticsCard
              title={{ text: 'TAKEDOWNS COMPLETED', fontWeight: 'bold' }}
                count={statsData.takedowns_completed?.toString() || '0'}
              icon={<IoCheckmarkDone size="22px" color="white" />}
              direction="right"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MiniStatisticsCard
              title={{ text: 'TAKEDOWNS PENDING', fontWeight: 'bold' }}
                count={statsData.takedowns_pending?.toString() || '0'}
              icon={<IoTime size="22px" color="white" />}
              direction="right"
            />
          </Grid>
        </Grid>
        )}

        {/* Charts Row */}
        {!loading && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%', padding: '24px' }}>
              <VuiBox>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <VuiTypography variant="lg" color="white" fontWeight="bold">
                    INFRINGEMENT ACTIVITY
                  </VuiTypography>
                  
                  <VuiBox display="flex" gap={2} alignItems="center">
                    <VuiBox width="175px">
                      <VuiSelect
                        options={infringementFilterOptions}
                        value={firstFilter}
                        onChange={handleFirstFilterChange}
                        placeholder="Select..."
                        isSearchable={false}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            backgroundColor: 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            minHeight: '32px',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            }
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: 'white',
                            fontSize: '.5rem',
                            fontWeight: 'medium'
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '12px'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#0075FF' 
                              : state.isFocused 
                                ? 'rgba(255, 255, 255, 0.1)' 
                                : 'transparent',
                            color: 'white',
                            fontSize: '0.5rem',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                          })
                        }}
                      />
                    </VuiBox>
                    
                    <VuiTypography variant="caption" color="text" fontWeight="medium">
                      BY
                    </VuiTypography>
                    
                    <VuiBox width="175px">
                      <VuiSelect
                        options={getSecondFilterOptions()}
                        value={secondFilter}
                        onChange={handleSecondFilterChange}
                        placeholder="Select..."
                        isSearchable={false}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            backgroundColor: '#0075FF',
                            border: 'none',
                            borderRadius: '8px',
                            minHeight: '32px',
                            '&:hover': {
                              backgroundColor: '#0056CC',
                            }
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'medium'
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '12px'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#0075FF' 
                              : state.isFocused 
                                ? 'rgba(255, 255, 255, 0.1)' 
                                : 'transparent',
                            color: 'white',
                            fontSize: '0.5rem',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                          })
                        }}
                      />
                    </VuiBox>
                  </VuiBox>
                </VuiBox>
                <VuiBox sx={{ minHeight: '300px' }}>
                  {(() => {
                    const hasData = currentChartData && currentChartData.data;
                    const hasLabels = currentChartData && currentChartData.labels;
                    const dataLength = currentChartData?.data?.length || 0;
                    const labelsLength = currentChartData?.labels?.length || 0;
                    const lengthsMatch = dataLength === labelsLength;
                    const allNumbers = currentChartData?.data?.every(val => typeof val === 'number' && !isNaN(val));
                    
                    console.log('Chart Render Validation:', {
                      hasData,
                      hasLabels,
                      dataLength,
                      labelsLength,
                      lengthsMatch,
                      allNumbers,
                      data: currentChartData?.data,
                      labels: currentChartData?.labels,
                      shouldRender: hasData && hasLabels && dataLength > 0 && labelsLength > 0 && lengthsMatch && allNumbers
                    });
                    
                    return (hasData && 
                     hasLabels && 
                     dataLength > 0 && 
                     labelsLength > 0 &&
                     lengthsMatch &&
                     allNumbers) ? (
                  <DefaultDoughnutChart
                    chartData={currentChartData.data}
                    chartOptions={{
                      ...donutChartOptions,
                      labels: currentChartData.labels,
                        colors: currentChartData.colors || [],
                      fill: {
                          colors: currentChartData.colors || [],
                      },
                      legend: {
                        ...donutChartOptions.legend,
                        markers: {
                          ...donutChartOptions.legend.markers,
                            fillColors: currentChartData.colors || [],
                        },
                      },
                        tooltip: getTooltipFormatter() || donutChartOptions.tooltip,
                    }}
                    />
                  ) : (
                    <VuiBox display="flex" justifyContent="center" alignItems="center" height="300px">
                      <VuiTypography color="text">No data available</VuiTypography>
                    </VuiBox>
                  );
                  })()}
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%', padding: '24px' }}>
              <VuiBox>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <VuiTypography variant="lg" color="white" fontWeight="bold">
                    TAKEDOWNS
                  </VuiTypography>
                  <VuiBox width="120px">
                    <VuiSelect
                      options={takedownsByOptions}
                      value={takedownsByFilter}
                      onChange={handleTakedownsByFilterChange}
                      placeholder="Select..."
                      isSearchable={false}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: '#0075FF',
                          border: 'none',
                          borderRadius: '8px',
                          minHeight: '32px',
                          '&:hover': {
                            backgroundColor: '#0056CC',
                          }
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: 'white',
                          fontSize: '0.5rem',
                          fontWeight: 'medium'
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.5rem'
                        }),
                        menu: (provided) => ({
                          ...provided,
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected 
                            ? '#0075FF' 
                            : state.isFocused 
                              ? 'rgba(255, 255, 255, 0.1)' 
                              : 'transparent',
                          color: 'white',
                          fontSize: '0.5rem',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        })
                      }}
                    />
                  </VuiBox>
                </VuiBox>
                <VuiBox sx={{ minHeight: '300px' }}>
                  <VerticalBarChart
                    chartData={currentTakedownsByChartData.data}
                    chartOptions={{
                      ...barChartOptions,
                      xaxis: {
                        ...barChartOptions.xaxis,
                        categories: currentTakedownsByChartData.categories,
                      },
                      fill: {
                        ...barChartOptions.fill,
                        gradient: {
                          ...barChartOptions.fill.gradient,
                          colorStops: [
                            [
                              {
                                offset: 0,
                                color: currentTakedownsByChartData.colors[0],
                                opacity: 1,
                              },
                              {
                                offset: 100,
                                color: currentTakedownsByChartData.colors[1],
                                opacity: 0.8,
                              },
                            ],
                          ],
                        },
                      },
                    }}
                  />
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
        )}

        {/* Bottom Row */}
        {!loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <ContentApproval />
          </Grid>
          <Grid item xs={12} lg={8}>
            <Highlights />
          </Grid>
          
          {/* <Grid item xs={12} lg={3.5}>
            <ComplianceUpdate />
          </Grid> */}
        </Grid>
        )}
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Overview;
